import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Button, notification, Space, Table, Tag, Typography } from 'antd';
import numeral from 'numeral';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DeleteOutlined, CreditCardOutlined } from '@ant-design/icons';
import { quittanceStatuses } from '../../utils/constants/tagColors';
import MercanetForm from './MercanetForm';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';

const { Column } = Table;

const QuittanceTable = ({ data: quittances, contractStatus, lazy = false }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const paymentStatus = params.get('paymentStatus');
  const { t } = useTranslation();
  const { message } = useErrorMessage();
  const { dispatchAPI } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState();
  const [seal, setSeal] = useState(null);
  const [currentYear, setCurrentYear] = useState();
  const [uniqueYears, setUniqueYears] = useState([]);
  const [selectedQuittances, setSelectedQuittances] = useState([]);
  const [selectedPaperWorkFee, setSelectedPaperWorkFee] = useState();

  const lineThroughStatus = [
    'UNPAID_2',
    'FORMAL_NOTICE',
    'WARRANTY_SUSPENSION',
    'TERMINATED_NON_PAYMENT'
  ];

  let total = 0;
  selectedQuittances.forEach((q) => {
    total += q.selling_price_customer + (q?.unpaid_fees || 0);
    if (
      q?.paperwork_fee_invoice?.paperwork_fee_payment_choices !== 'FREE' &&
      q?.paperwork_fee_invoice?.paperworkFee_status === 'NOT_PAID'
    ) {
      total += q.paperwork_fee_invoice.amount;
    }
    if (q?.deposit) {
      total += q.deposit;
    }
  });

  const getSeal = async () => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('POST', {
        url: 'mercanet-payments/seal',
        body: values
      });
      setSeal(data);
    } catch (e) {
      message(e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (values)
      (async () => {
        await getSeal();
      })();
    // eslint-disable-next-line
  }, [values]);

  useEffect(() => {
    if (paymentStatus === '1')
      notification.success({
        message: 'Paiement réussi',
        key: 'successPayment',
        duration: 10
      });
  }, [paymentStatus]);

  useEffect(() => {
    if (quittances.length) {
      const years = quittances.map((q) => moment(q.period_date).year());
      setUniqueYears([...new Set(years)]);
    }
  }, [quittances]);

  useEffect(() => {
    if (uniqueYears.length) setCurrentYear(uniqueYears[uniqueYears.length - 1]);
  }, [uniqueYears]);

  useEffect(() => {
    if (selectedQuittances.length) {
      setValues({
        selectedQuittances: selectedQuittances.map((q) => q.key),
        origin: window.location.href
      });
    } else notification.destroy();
    // eslint-disable-next-line
  }, [selectedQuittances.length]);

  useEffect(() => {
    if (seal) {
      const quittanceNumber = selectedQuittances.length;
      notification.open({
        key: 'mercanet',
        message: (
          <Typography.Text>
            {`${quittanceNumber} quittance${
              quittanceNumber > 1 ? 's' : ''
            } sélectionnée${
              quittanceNumber > 1 ? 's' : ''
            } pour un total de ${numeral(total).format('0,0.00')}€`}
          </Typography.Text>
        ),
        description: (
          <MercanetForm
            seal={seal}
            isLoading={isLoading}
            selectedQuittances={selectedQuittances}
            contractStatus={contractStatus}
            allQuittances={quittances}
          />
        ),
        duration: 0,
        closeIcon: (
          <DeleteOutlined style={{ color: 'var(--secondaryColor)' }} />
        ),
        onClose: () => {
          setSelectedQuittances([]);
          setSeal(null);
        }
      });
    } // eslint-disable-next-line
  }, [seal, isLoading]);

  const shouldEnablePayButton = ({ key }) => {
    if (selectedQuittances.length) {
      if (selectedQuittances.map((q) => q.key).includes(key)) {
        return false;
      }
      const nextQuittanceToPay = quittances.find(
        (q) =>
          q.quittance_status === 'NOT_PAID' &&
          !selectedQuittances.map((s) => s.key).includes(q._id) &&
          moment(q.period_date).isSameOrAfter(
            moment(
              selectedQuittances[selectedQuittances.length - 1].period_date
            )
          )
      );
      return nextQuittanceToPay?._id === key;
    }
    const nextQuittanceToPay = quittances.find(
      (q) => q.quittance_status === 'NOT_PAID'
    );
    return nextQuittanceToPay?._id === key;
  };

  return (
    <>
      {!lazy && (
        <Space style={{ margin: '4px 0 16px 0' }}>
          {uniqueYears
            .sort((a, b) => a - b)
            .map((year) => (
              <Button
                key={year}
                type={year === currentYear ? 'primary' : 'link'}
                onClick={() => setCurrentYear(year)}
              >
                {year}
              </Button>
            ))}
        </Space>
      )}
      <Table
        dataSource={quittances
          .filter(
            (q) =>
              moment(q.period_date).year() === currentYear &&
              q.quittance_status !== 'PENDING' &&
              q.quittance_status !== 'PROCESSED'
          )
          .map(({ _id: key, ...d }) => ({ ...d, key }))}
        pagination={{ pageSize: 13, hideOnSinglePage: true }}
        scroll={{ x: 900 }}
        rowClassName={(record) =>
          record.rejection_code_1 &&
          !record.price_includes_previous_quittance &&
          lineThroughStatus.includes(contractStatus)
            ? 'table-row-strikethrough'
            : ''
        }
      >
        <Column
          title={t('quittances.form.period_date')}
          dataIndex="period_date"
          render={(date) => date && moment(date).format('MM/YYYY')}
        />
        <Column
          title={t('quittances.form.quittance_status')}
          dataIndex="quittance_status"
          render={(quittance_status, quittance) => (
            <Tag
              color={
                !quittance.rejection_code_1
                  ? quittanceStatuses[quittance_status]
                  : 'red'
              }
            >
              {t(`quittances.tags.${quittance_status}`)}
            </Tag>
          )}
        />
        <Column
          title={t('quittances.form.selling_price_customer')}
          ellipsis
          dataIndex="selling_price_customer"
          render={(p, quittance) => {
            return (quittance?.paperwork_fee_invoice &&
              quittance?.paperwork_fee_invoice
                ?.paperwork_fee_payment_choices !== 'FREE' &&
              quittance?.paperwork_fee_invoice?.paperworkFee_status ===
                'NOT_PAID') ||
              quittance.deposit > 0
              ? `${numeral(
                  (p || 0) +
                    (quittance?.paperwork_fee_invoice?.amount || 0) +
                    (quittance?.deposit || 0)
                ).format('0,0.00')}€${
                  quittance?.paperwork_fee_invoice?.amount > 0
                    ? ` (dont ${numeral(
                        quittance?.paperwork_fee_invoice?.amount
                      ).format('0,0.00')}€ de FDD)`
                    : ''
                }${
                  quittance?.deposit > 0
                    ? ` (dont ${numeral(quittance?.deposit).format(
                        '0,0.00'
                      )}€ de caution)`
                    : ''
                }`
              : `${numeral(p + quittance.intervention_fees || 0).format(
                  '0,0.00'
                )}€`;
          }}
        />
        {!lazy && (
          <>
            <Column
              title={t('quittances.form.unpaid_fees')}
              dataIndex="unpaid_fees"
              render={(p) => `${numeral(p || 0).format('0,0.00')}€`}
            />
            <Column
              title={t('quittances.form.refund')}
              dataIndex="refund"
              render={(p, quittance) =>
                `${numeral(p - quittance.intervention_fees || 0).format(
                  '0,0.00'
                )}€`
              }
            />
            <Column
              title={t('quittances.form.rejection_code_1')}
              dataIndex="rejection_code_1"
              render={(rejection_code_1) =>
                rejection_code_1
                  ? t(`overdues.reasons.${rejection_code_1}`)
                  : []
              }
            />
          </>
        )}
        <>
          <Column
            title={t('quittances.form.quittance_paid_by')}
            ellipsis
            dataIndex="quittance_paid_by"
            render={(quittance_paid_by) =>
              quittance_paid_by && (
                <Tag>{t(`contracts.form.options.${quittance_paid_by}`)}</Tag>
              )
            }
          />
          <Column
            {...(lazy ? {} : { fixed: 'right' })}
            render={(quittance) => {
              if (
                selectedQuittances[selectedQuittances.length - 1]?.key ===
                quittance.key
              )
                return (
                  <Button
                    type="default"
                    danger
                    onClick={() => {
                      setSelectedPaperWorkFee(
                        quittance?.paperwork_fee_invoice
                          ? null
                          : selectedPaperWorkFee
                      );
                      setSelectedQuittances(
                        selectedQuittances.slice(
                          0,
                          selectedQuittances.length - 1
                        )
                      );
                    }}
                  >
                    Retirer du panier
                  </Button>
                );
              if (quittance.quittance_status === 'NOT_PAID')
                return (
                  <Button
                    type="primary"
                    disabled={!shouldEnablePayButton(quittance)}
                    onClick={() => {
                      setSelectedPaperWorkFee(
                        quittance?.paperwork_fee_invoice
                          ? quittance?.paperwork_fee_invoice?._id
                          : selectedPaperWorkFee
                      );
                      setSelectedQuittances([...selectedQuittances, quittance]);
                    }}
                  >
                    {selectedQuittances.length ? (
                      'Ajouter au panier'
                    ) : (
                      <>
                        {t('paperWorkFee.form.paid')}
                        <CreditCardOutlined />
                      </>
                    )}
                  </Button>
                );
              return '';
            }}
          />
        </>
      </Table>
    </>
  );
};

QuittanceTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  contractStatus: PropTypes.string.isRequired,
  lazy: PropTypes.bool
};

QuittanceTable.defaultProps = {
  lazy: false
};

QuittanceTable.defaultProps = {
  data: []
};

export default QuittanceTable;
